import { isErrorLike } from '@apollo/client/errors';
import { useMutation } from '@apollo/client/react';

import { DISCOUNT_IDS } from '@constants/apiConstants';
import {
  AgentSendEeccContractInput,
  BundleQuoteFragment,
  ProductType,
} from '@generated/gql/graphql';
import { SnackbarVariant, useSnackbar } from '@hooks/useSnackbar';
import { useSnackbarError } from '@hooks/useSnackbarErrors';
import { YourDetailsFormValues } from '@src/types/yourDetails';

import { agentSendEeccContract } from '../query';

const useSendEeccContract = (
  basketId: string,
  formValues: YourDetailsFormValues,
  selectedQuotes: BundleQuoteFragment[]
) => {
  const [sendContract, { loading, error }] = useMutation(agentSendEeccContract);
  const queueSnackbarError = useSnackbarError();
  const snackbar = useSnackbar();

  const input: AgentSendEeccContractInput = {
    basketId,
    email: formValues.aboutEmail,
    title: formValues.aboutNameTitle,
    firstName: formValues.aboutNameFirst,
    lastName: formValues.aboutNameLast,
  };

  const broadbandQuote = selectedQuotes.find(
    (quote: BundleQuoteFragment) => quote.product.type === ProductType.Broadband
  );

  const mobileQuotes = selectedQuotes.filter(
    (quote: BundleQuoteFragment) => quote.product.type === ProductType.Mobile
  );

  if (broadbandQuote) {
    input.fixedLineProductDetails = {
      quoteId: broadbandQuote.id,
      //forceNoLineInstallation
      tryBeforeYouBuy: broadbandQuote.discounts.some(
        (discount) =>
          discount.id === DISCOUNT_IDS.TBYB_BROADBAND_LINE_DISCOUNT &&
          discount.qualified
      ),
    };
  }

  if (mobileQuotes.length > 0) {
    input.mobileProductDetails = mobileQuotes.map(
      (quote: BundleQuoteFragment) => {
        return {
          quoteId: quote.id,
        };
      }
    );
  }

  return {
    loading,
    error,
    sendContract: async () => {
      try {
        await sendContract({
          variables: {
            input,
          },
          onCompleted: () => {
            snackbar({
              message: 'Contract sent',
              variant: SnackbarVariant.Success,
            });
          },
        });
      } catch (error) {
        if (isErrorLike(error)) {
          queueSnackbarError(error);
        }
      }
    },
  };
};

export { useSendEeccContract };
